class DiscountPopup extends HTMLElement {
  static STORAGE_TAB_UNTIL = 'starucraft_discount_tab_dismissed_until';
  static STORAGE_TAB_SESSION = 'starucraft_discount_tab_dismissed_session';
  static STORAGE_AUTO_OPENED = 'starucraft_discount_auto_opened_session';
  static STORAGE_CLAIMED = 'starucraft_discount_claimed';
  static STORAGE_TAB_LEGACY = 'starucraft_discount_tab_dismissed';
  static TAB_COOLDOWN_MS = 24 * 60 * 60 * 1000;

  connectedCallback() {
    this.code = this.dataset.discountCode || '';
    this.displayCode = this.dataset.discountCodeDisplay || this.code;
    this.autoOpenDelay = Number(this.dataset.autoOpenDelay || 0);
    this.customerTag = this.dataset.customerTag || 'newsletter,discount-popup';

    this.tab = this.querySelector('[data-discount-tab]');
    this.dialog = this.querySelector('[data-discount-dialog]');
    this.form = this.querySelector('[data-discount-form]');
    this.formStep = this.querySelector('[data-discount-form-step]');
    this.successStep = this.querySelector('[data-discount-success-step]');
    this.emailInput = this.querySelector('[data-discount-email]');
    this.submitBtn = this.querySelector('[data-discount-submit]');
    this.errorEl = this.querySelector('[data-discount-error]');
    this.codeDisplay = this.querySelector('[data-discount-code-display]');
    this.copyBtn = this.querySelector('[data-discount-copy]');

    if (!this.code || !this.dialog) return;

    this.#migrateLegacyStorage();
    this.#bindEvents();
    this.#initVisibility();
  }

  #migrateLegacyStorage() {
    try {
      if (localStorage.getItem(DiscountPopup.STORAGE_TAB_LEGACY) !== '1') return;
      localStorage.removeItem(DiscountPopup.STORAGE_TAB_LEGACY);
      if (!localStorage.getItem(DiscountPopup.STORAGE_TAB_UNTIL)) {
        localStorage.setItem(
          DiscountPopup.STORAGE_TAB_UNTIL,
          String(Date.now() + DiscountPopup.TAB_COOLDOWN_MS)
        );
      }
    } catch {
      /* blocked storage */
    }
  }

  #bindEvents() {
    this.tab?.addEventListener('click', (event) => {
      if (event.target.closest('[data-discount-tab-close]')) return;
      this.#openDialog();
    });

    this.querySelector('[data-discount-tab-close]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#dismissTab();
    });

    this.querySelector('[data-discount-dialog-close]')?.addEventListener('click', () => {
      this.#closeDialog();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleSubmit();
    });

    this.querySelector('[data-discount-apply]')?.addEventListener('click', () => {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/discount/${encodeURIComponent(this.code)}?redirect=${redirect}`;
    });

    this.copyBtn?.addEventListener('click', () => this.#copyCode());

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.#closeDialog();
    });

    this.dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeDialog();
    });
  }

  #initVisibility() {
    if (this.#isClaimed()) {
      this.#showClaimedState();
    }

    if (this.#isTabDismissed()) {
      this.#hideTab();
    }

    this.#scheduleAutoOpen();
  }

  #scheduleAutoOpen() {
    if (this.autoOpenDelay <= 0) return;
    if (this.#isClaimed()) return;
    if (this.#isTabDismissed()) return;
    if (this.#wasAutoOpenedThisSession()) return;
    if (this.#isWithinAutoOpenCooldown()) return;

    window.setTimeout(() => {
      if (this.dialog?.open) return;
      if (this.#isTabDismissed()) return;
      this.#markAutoOpenedThisSession();
      this.#openDialog();
    }, this.autoOpenDelay * 1000);
  }

  #dismissTab() {
    try {
      sessionStorage.setItem(DiscountPopup.STORAGE_TAB_SESSION, '1');
      localStorage.setItem(
        DiscountPopup.STORAGE_TAB_UNTIL,
        String(Date.now() + DiscountPopup.TAB_COOLDOWN_MS)
      );
    } catch {
      /* Safari private mode */
    }
    this.#closeDialog();
    this.#hideTab();
  }

  #isClaimed() {
    try {
      return localStorage.getItem(DiscountPopup.STORAGE_CLAIMED) === '1';
    } catch {
      return false;
    }
  }

  #isTabDismissedThisSession() {
    try {
      return sessionStorage.getItem(DiscountPopup.STORAGE_TAB_SESSION) === '1';
    } catch {
      return false;
    }
  }

  #isWithinAutoOpenCooldown() {
    try {
      const until = Number(localStorage.getItem(DiscountPopup.STORAGE_TAB_UNTIL) || 0);
      if (!until) return false;
      if (Date.now() >= until) {
        localStorage.removeItem(DiscountPopup.STORAGE_TAB_UNTIL);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  #isTabDismissed() {
    return this.#isTabDismissedThisSession();
  }

  #wasAutoOpenedThisSession() {
    try {
      return sessionStorage.getItem(DiscountPopup.STORAGE_AUTO_OPENED) === '1';
    } catch {
      return false;
    }
  }

  #markAutoOpenedThisSession() {
    try {
      sessionStorage.setItem(DiscountPopup.STORAGE_AUTO_OPENED, '1');
    } catch {
      /* blocked storage */
    }
  }

  #showClaimedState() {
    if (this.formStep) this.formStep.hidden = true;
    if (this.successStep) this.successStep.hidden = false;
    if (this.codeDisplay) this.codeDisplay.textContent = this.displayCode;
  }

  #hideTab() {
    this.setAttribute('data-hidden', 'true');
  }

  #openDialog() {
    if (this.#isClaimed()) this.#showClaimedState();
    if (this.dialog && !this.dialog.open) this.dialog.showModal();
  }

  #closeDialog() {
    if (this.dialog?.open) this.dialog.close();
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async #subscribeEmail(email) {
    const root = window.Shopify?.routes?.root || '/';
    const body = new URLSearchParams({
      form_type: 'customer',
      utf8: '✓',
      'contact[email]': email,
      'contact[tags]': this.customerTag,
    });

    try {
      await fetch(`${root}contact`, { method: 'POST', body, headers: { Accept: 'text/html' } });
    } catch {
      /* still reveal code */
    }
  }

  async #handleSubmit() {
    const email = (this.emailInput?.value || '').trim();
    this.#hideError();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError('Please enter a valid email address.');
      this.emailInput?.focus();
      return;
    }

    if (this.submitBtn) this.submitBtn.disabled = true;
    await this.#subscribeEmail(email);
    if (this.submitBtn) this.submitBtn.disabled = false;

    try {
      localStorage.setItem(DiscountPopup.STORAGE_CLAIMED, '1');
    } catch {
      /* blocked storage */
    }

    this.#showClaimedState();
  }

  async #copyCode() {
    try {
      await navigator.clipboard.writeText(this.displayCode);
      if (this.copyBtn) {
        const original = this.copyBtn.textContent;
        this.copyBtn.textContent = 'Copied!';
        window.setTimeout(() => {
          this.copyBtn.textContent = original;
        }, 2000);
      }
    } catch {
      /* code still visible */
    }
  }
}

if (!customElements.get('discount-popup')) {
  customElements.define('discount-popup', DiscountPopup);
}
